import React, { FC, useCallback, useMemo, useState } from 'react';
import { withRouter } from 'react-router';
import { connect } from 'react-redux';
import {
	getCurrency,
	getEntityById,
	nullSafeGet,
	nullSafeGetOrElse,
} from '../../utils/DataAccessUtils';
import { EmptyState } from '../empty_state/EmptyState';
import PaginatedReduxTableWithHeader from '../common/PaginatedReduxTableWithHeader';
import {
	accAuditsCsvReportGenerator,
	accAuditsRestCrudThunksForBuyer,
	accAuditsRestCrudThunksForSupplier,
	updateChangeApplyStatus,
} from '../../thunks/acc_audits_thunks';
import { ACC_AUDITS_CRUD_ACTION_CREATORS } from '../../actions/acc_audits_actions';
import {
	ASSET_FIELDS_DETAILS,
	ASSET_FIELDS_LABELS_MAP,
	getAssetNumber,
} from '../../utils/assetUtils';
import { Button, Layout, Menu, message, Popover, Row, Spin, Table } from 'antd';
import GeneralStatusDisplay from '../AdminCycleEventsIndexPage/GeneralStatusDisplay';
import {
	ACC_AUDIT_ACTIONABLE_STATUSES,
	ACC_AUDIT_CHANGE_APPLY_STATUSES,
	ACC_AUDIT_KEY_DISPLAY_NAMES,
	ACC_AUDIT_MATCH_STATUSES,
} from '../../utils/AssetCycleUtils';
import ACCAuditNeedsVerificationAction from './ACCAuditNeedsVerificationAction';
import ACCAuditMissingAssetAction from './ACCAuditMissingAssetAction';
import ACCAuditChangeActions from './ACCAuditChangeActions';
import ACCAuditApproveAllAction from './ACCAuditApproveAllAction';
import { DownloadOutlined, EditOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { ROLE_TYPES } from '../../utils/DataConstants';
import AssetAuditForm from './AssetAuditForm';
import {
	cycleRunsRestCrudThunksForBuyer,
	cycleRunsRestCrudThunksForSupplier,
} from '../../thunks/cycle_runs_thunks';
import { withUserType } from '../withUserType';
import moment from 'moment';

const { Content } = Layout;

const ACC_AUDITS_TARGET_COLLECTION = 'accAuditsIndex';

const ACCRunDetailDetailsPage: FC<any> = ({
	fetching,
	match,
	cycleRun,
	accAudits,
	updateFilters,
	clearAndUpdateFilters,
	fetchACCAudits,
	exportCsv,
	updateAssetAudit,
	completeCycleRunAssetAudit,
	fetchCycleRun,
	userType,
	currentUser,
}): React.ReactElement => {
	const [assetAuditFormVisible, setAssetAuditFormVisible] = useState(null);

	const currency = useMemo(() => getCurrency({ currentUser }), [currentUser]);
	const allAudits = useMemo(
		() =>
			nullSafeGetOrElse('assetCountBasedOnAssetType', cycleRun, []).reduce((acc, ac) => {
				return acc.concat(nullSafeGetOrElse('assetCycleCountAudits', ac, []));
			}, []),
		[cycleRun]
	);

	const [exportingCsv, setExportingCsv] = useState(false);

	const getStatusField = useCallback(
		(record) => {
			const matchStatusDisplay = (
				<GeneralStatusDisplay
					statusKey={record.matchStatus}
					statusLabel={nullSafeGetOrElse(
						record.matchStatus,
						ACC_AUDIT_KEY_DISPLAY_NAMES,
						record.matchStatus
					)}
				/>
			);
			const locationTransferMessage = record.isLocationTransferRequired ? (
				<div className="flex flex-row items-center gap-2">
					<div className="bodymedium underline">Location transfer:</div>
					<div className="bodyregular">{`${nullSafeGetOrElse(
						'transferFromLocation.name',
						record,
						'--'
					)} -> ${nullSafeGetOrElse('location.name', cycleRun, '--')}`}</div>
				</div>
			) : null;

			const fieldsWithVariance = nullSafeGetOrElse('fieldsWithVariance', record, []).map(
				(field, idx) => {
					if (field.key === 'assetUUID') {
						return null;
					}
					const fieldDetails = ASSET_FIELDS_DETAILS.find((f) => f.key === field.key);
					const formatValue = (value: any, fieldDetails: any) => {
						if (!fieldDetails) return value;
						switch (fieldDetails.type) {
							case 'text':
								return value ? value : '--';
							case 'textScanner':
								return value ? value : '--';
							case 'switch':
								return value ? 'Yes' : 'No';
							case 'date':
								return value ? moment(value).format('MM/DD/YYYY') : '--';
							case 'currency':
								return value ? currency.format(value) : '--';
							default:
								return value ? value : '--';
						}
					};

					const formattedOldValue = formatValue(nullSafeGet('oldValue', field), fieldDetails);
					const formattedNewValue = formatValue(nullSafeGet('newValue', field), fieldDetails);

					return (
						<div key={`${record.assetId}-fv-${idx}`} className="flex flex-col gap-2">
							<div className="flex flex-row items-center gap-2">
								<div className="bodymedium underline">{`${
									ASSET_FIELDS_LABELS_MAP[field.key] || field.key
								}:`}</div>
								<div className="bodymedium">{`Exisiting: ${formattedOldValue}`}</div>
							</div>
							<div className="bodymedium">{`Entered: ${formattedNewValue}`}</div>
						</div>
					);
				}
			);

			const incorrectAssetShown = nullSafeGet('wronglyMappedAsset', record) ? (
				<div className="flex flex-col gap-2">
					<div className="flex flex-row items-center gap-2">
						<div className="bodymedium underline">Incorrect asset shown:</div>
						<div className="bodymedium">
							{nullSafeGetOrElse('wronglyMappedAsset.name', record, '--')}
						</div>
					</div>
					<div className="flex flex-row items-center gap-2 text-[#00000073]">
						<div className="bodyregular">{`Asset No: ${nullSafeGetOrElse(
							'wronglyMappedAsset.assetNumber',
							record,
							'--'
						)}`}</div>
						<div className="bodyregular">{`ID: ${getAssetNumber(
							nullSafeGet('wronglyMappedAsset', record)
						)}`}</div>
					</div>
				</div>
			) : null;

			const transferObject = nullSafeGetOrElse(
				'transferToLocation',
				record,
				nullSafeGet('transferToStockLocation', record)
			);
			const transferText = transferObject ? (
				<div className="flex flex-row items-center gap-2">
					<div className="bodymedium underline">Transfer to:</div>
					<div className="bodymedium">{nullSafeGetOrElse('name', transferObject, '--')}</div>
				</div>
			) : null;

			const additionalNotes = nullSafeGetOrElse('notes', record, null) ? (
				<div className="flex flex-col items-start gap-1 rounded-md bg-grey-3 px-2 py-3">
					<div className="bodymedium ">Additional Notes</div>
					<div className="bodyregular">{nullSafeGetOrElse('notes', record, '--')}</div>
				</div>
			) : null;
			const actionLabels = {
				assignQRCodeAndRemoveFromMatch: 'Mark to assign QR & remove from match',
				swapQRCodes: 'Swap QR codes',
				applyNewQRCode: 'Mark to apply new QR code',
				needsFurtherReview: 'Needs further review',
				qrCodeRemoved: 'QR code removed',
			};

			return (
				<div className="flex flex-col justify-start gap-2">
					<div className="flex flex-row items-center gap-2">
						{matchStatusDisplay}
						{record.isLocationTransferRequired ? (
							<div className="footnoteMedium text-[#DB8556]">Location transfer</div>
						) : null}
						{record.matchStatus === ACC_AUDIT_MATCH_STATUSES.MatchedWithVariance &&
						nullSafeGet('needsVerificationAction', record) &&
						actionLabels[record.needsVerificationAction] ? (
							<div className="footnoteMedium text-[#DB8556]">
								{actionLabels[record.needsVerificationAction]}
							</div>
						) : null}
						<ACCAuditNeedsVerificationAction accAudit={record} />
						<ACCAuditMissingAssetAction accAudit={record} />
						{nullSafeGet('changeApplyStatus', record) ===
						ACC_AUDIT_CHANGE_APPLY_STATUSES.RunInProgress ? (
							<span
								style={{
									color: '#555',
									background: 'rgba(0, 0, 0, 0.1)',
									padding: '4px 12px',
									borderRadius: 16,
									whiteSpace: 'nowrap',
								}}
							>
								Draft
							</span>
						) : null}
					</div>
					{transferText}
					{locationTransferMessage}
					{incorrectAssetShown}
					{fieldsWithVariance}
					{additionalNotes}
				</div>
			);
		},
		[cycleRun]
	);

	const getActions = useCallback((record) => {
		const statusField = ACC_AUDIT_ACTIONABLE_STATUSES.includes(record.changeApplyStatus) ? null : (
			<GeneralStatusDisplay
				statusKey={record.changeApplyStatus}
				statusLabel={nullSafeGetOrElse(
					record.changeApplyStatus,
					ACC_AUDIT_KEY_DISPLAY_NAMES,
					record.changeApplyStatus
				)}
			/>
		);

		return (
			<div className="flex flex-col">
				<div className="flex flex-row items-start gap-2">
					{statusField}
					{record.changeApplyNote ? (
						<Popover content={record.changeApplyNote} trigger="hover">
							<InfoCircleOutlined style={{ marginLeft: '12px' }} />
						</Popover>
					) : null}
					<ACCAuditChangeActions accAudit={record} />
					{record.changeApplyStatus !== ACC_AUDIT_CHANGE_APPLY_STATUSES.ChangesApplied ? (
						<Button icon={<EditOutlined />} onClick={() => setAssetAuditFormVisible(record)} />
					) : null}
				</div>
			</div>
		);
	}, []);

	const columns = useMemo(
		() => [
			{
				title: 'Name',
				dataIndex: ['asset', 'name'],
				render: (text, record) => {
					return (
						<div className="flex flex-row items-center gap-2">
							<div className="bodymedium">{text},</div>
							<div className="bodyregular">
								{nullSafeGetOrElse('asset.assetType.name', record, '')}
							</div>
						</div>
					);
				},
			},
			{
				title: 'Asset No.',
				dataIndex: ['asset', 'assetNumber'],
			},
			{
				title: 'Asset ID',
				dataIndex: 'asset',
				render: getAssetNumber,
			},
			{
				title: 'Status',
				render: getStatusField,
			},
			{
				title: 'Actions',
				render: getActions,
			},
		],
		[getActions, getStatusField]
	);

	const getStateSliceProp = useCallback(
		(propName) => nullSafeGetOrElse(`${ACC_AUDITS_TARGET_COLLECTION}.${propName}`, accAudits, {}),
		[accAudits]
	);

	const refreshAudits = useCallback(() => {
		fetchACCAudits(
			{},
			ACC_AUDITS_TARGET_COLLECTION,
			getStateSliceProp('pagination'),
			getStateSliceProp('sorting'),
			getStateSliceProp('filters')
		);
		fetchCycleRun(nullSafeGet('id', cycleRun));
	}, [fetchACCAudits, getStateSliceProp, fetchCycleRun, cycleRun]);

	const downloadCSV = useCallback(() => {
		setExportingCsv(true);
		exportCsv({
			...getStateSliceProp('sorting'),
			...getStateSliceProp('filters'),
		}).finally(() => setExportingCsv(false));
	}, [exportCsv, getStateSliceProp]);

	return (
		<>
			{assetAuditFormVisible ? (
				<AssetAuditForm
					assetAudit={assetAuditFormVisible}
					onSubmit={(values) => {
						updateAssetAudit({ ...assetAuditFormVisible, ...values }).then((newAssetAudit) => {
							completeCycleRunAssetAudit(newAssetAudit);
							setAssetAuditFormVisible(null);
						});
					}}
					onCancel={() => setAssetAuditFormVisible(null)}
					cycleRun={cycleRun}
				/>
			) : null}
			<PaginatedReduxTableWithHeader
				targetCollectionName={ACC_AUDITS_TARGET_COLLECTION}
				stateSlice={accAudits}
				entityCollectionName="acc_audits"
				updateFilters={updateFilters}
				clearAndUpdateFilters={clearAndUpdateFilters}
				handleQueryParams
				filterConfig={[]}
				updateQueryParams={true}
				rightActions={
					<div className="flex flex-row items-center gap-2">
						<Popover content="Export CSV" trigger="hover">
							<Button
								size="large"
								icon={<DownloadOutlined />}
								className="inline-block-visible-md ml-2"
								onClick={downloadCSV}
								loading={exportingCsv}
							>
								<span className="inline-block-visible-xxl">Export CSV</span>
							</Button>
						</Popover>
						<ACCAuditApproveAllAction onSuccess={refreshAudits} />
					</div>
				}
				emptyState={
					<EmptyState
						graphic={
							<img
								alt="Currently there are no assets!"
								style={{ marginBottom: 8 }}
								src="https://s3.amazonaws.com/mock-data-assets/categories/images/cactus.svg"
							/>
						}
						headline={"It's a little lonely in here."}
						body={
							<div style={{ textAlign: 'center' }}>
								<div style={{ maxWidth: 440, marginBottom: 16 }}>
									Looks like your team has not logged any assets yet.
								</div>
							</div>
						}
					/>
				}
				showHeader
				tableColumns={columns}
				preAppliedFilters={{ assetCycleCountRunId: match.params.id }}
				fetchData={
					userType === ROLE_TYPES.SUPPLIER
						? accAuditsRestCrudThunksForSupplier.read
						: accAuditsRestCrudThunksForBuyer.read
				}
			/>
		</>
	);
};

const mapStateToProps = (state, ownProps) => ({
	cycleRun: getEntityById(ownProps.match.params.id, state.cycle_runs),
	accAudits: state.acc_audits,
	fetching: state.acc_audits.fetching,
	currentUser: state.session.currentUser,
});

const mapDispatchToProps = (dispatch, ownProps) => ({
	updateFilters: (filters, targetCollection) =>
		dispatch(ACC_AUDITS_CRUD_ACTION_CREATORS.updateFilters(filters, targetCollection)),
	clearAndUpdateFilters: (filters, targetCollectionName) =>
		dispatch(ACC_AUDITS_CRUD_ACTION_CREATORS.clearAndUpdateFilters(filters, targetCollectionName)),
	fetchACCAudits: (
		params,
		targetCollectionName,
		pagination,
		sorting,
		filters,
		addToTargetCollection
	) =>
		dispatch(
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? accAuditsRestCrudThunksForSupplier.read(
						params,
						targetCollectionName,
						pagination,
						sorting,
						filters,
						addToTargetCollection
				  )
				: accAuditsRestCrudThunksForBuyer.read(
						params,
						targetCollectionName,
						pagination,
						sorting,
						filters,
						addToTargetCollection
				  )
		),
	exportCsv: (params) => dispatch(accAuditsCsvReportGenerator(ownProps.userType)(params)),
	updateAssetAudit: (assetAudit) =>
		dispatch(
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? accAuditsRestCrudThunksForSupplier.update(assetAudit)
				: accAuditsRestCrudThunksForBuyer.update(assetAudit)
		),
	completeCycleRunAssetAudit: (entity) =>
		dispatch(
			updateChangeApplyStatus(ownProps.userType)(entity, ACC_AUDIT_CHANGE_APPLY_STATUSES.Logged)
		),
	fetchCycleRun: (cycleRunId) =>
		dispatch(
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? cycleRunsRestCrudThunksForSupplier.read(cycleRunId)
				: cycleRunsRestCrudThunksForBuyer.read(cycleRunId)
		),
});

export default withRouter(
	withUserType(connect(mapStateToProps, mapDispatchToProps)(ACCRunDetailDetailsPage as any))
);
